<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { VARIANT_OPTIONS, COLOR_OPTIONS, SIZE_OPTIONS } from './constants.js';

const props = defineProps({
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  color: {
    type: String,
    default: null,
    validator: v => COLOR_OPTIONS.includes(v) || v === null,
  },
  variant: {
    type: String,
    default: null,
    validator: v => VARIANT_OPTIONS.includes(v) || v === null,
  },
  size: {
    type: String,
    default: null,
    validator: v => SIZE_OPTIONS.includes(v) || v === null,
  },
  disabled: { type: Boolean, default: false },
  menuItems: { type: Array, default: () => [] },
});

const emit = defineEmits(['click', 'action']);

const isOpen = ref(false);

const computedColor = computed(() => props.color ?? 'blue');
const computedVariant = computed(() => props.variant ?? 'solid');
const computedSize = computed(() => props.size ?? 'md');

const BUTTON_PART_CLASSES = {
  blue: {
    solid:
      'bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110',
    faded:
      'bg-n-brand/10 text-n-blue-11 hover:enabled:bg-n-brand/20 focus-visible:bg-n-brand/20',
    outline:
      'text-n-blue-11 hover:enabled:bg-n-brand/10 focus-visible:bg-n-brand/10',
    ghost:
      'text-n-blue-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
  },
  ruby: {
    solid:
      'bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10 focus-visible:bg-n-ruby-10',
    faded:
      'bg-n-ruby-9/10 text-n-ruby-11 hover:enabled:bg-n-ruby-9/20 focus-visible:bg-n-ruby-9/20',
    outline:
      'text-n-ruby-11 hover:enabled:bg-n-ruby-9/10 focus-visible:bg-n-ruby-9/10',
    ghost:
      'text-n-ruby-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
  },
  amber: {
    solid:
      'bg-n-amber-9 text-white hover:enabled:bg-n-amber-10 focus-visible:bg-n-amber-10',
    faded:
      'bg-n-amber-9/10 text-n-slate-12 hover:enabled:bg-n-amber-9/20 focus-visible:bg-n-amber-9/20',
    outline:
      'text-n-amber-11 hover:enabled:bg-n-amber-9/10 focus-visible:bg-n-amber-9/10',
    ghost:
      'text-n-amber-9 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
  },
  slate: {
    solid:
      'bg-n-button-color text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
    faded:
      'bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20',
    outline:
      'text-n-slate-11 hover:enabled:bg-n-slate-9/10 focus-visible:bg-n-slate-9/10',
    ghost:
      'text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
  },
  teal: {
    solid:
      'bg-n-teal-9 text-white hover:enabled:bg-n-teal-10 focus-visible:bg-n-teal-10',
    faded:
      'bg-n-teal-9/10 text-n-teal-11 hover:enabled:bg-n-teal-9/20 focus-visible:bg-n-teal-9/20',
    outline:
      'text-n-teal-11 hover:enabled:bg-n-teal-9/10 focus-visible:bg-n-teal-9/10',
    ghost:
      'text-n-teal-9 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2',
  },
};

const WRAPPER_OUTLINE = {
  blue: {
    solid: '',
    faded: '',
    outline: 'outline outline-1 outline-n-brand',
    ghost: '',
  },
  ruby: {
    solid: '',
    faded: '',
    outline: 'outline outline-1 outline-n-ruby-8',
    ghost: '',
  },
  amber: {
    solid: '',
    faded: '',
    outline: 'outline outline-1 outline-n-amber-9',
    ghost: '',
  },
  slate: {
    solid: 'outline outline-1 outline-n-container',
    faded: '',
    outline: 'outline outline-1 outline-n-strong',
    ghost: '',
  },
  teal: {
    solid: '',
    faded: '',
    outline: 'outline outline-1 outline-n-teal-9',
    ghost: '',
  },
};

const SIZE_CONFIG = {
  xs: {
    main: 'h-6 px-2',
    toggle: 'h-6 w-7',
    text: 'text-xs',
    chevron: 'size-4',
  },
  sm: {
    main: 'h-8 px-3',
    toggle: 'h-8 w-8',
    text: 'text-sm',
    chevron: 'size-5',
  },
  md: {
    main: 'h-8 px-4',
    toggle: 'h-8 w-8',
    text: 'text-sm font-medium',
    chevron: 'size-6',
  },
  lg: {
    main: 'h-12 px-5',
    toggle: 'h-12 w-10',
    text: 'text-base',
    chevron: 'size-6',
  },
};

const buttonPartClasses = computed(
  () => BUTTON_PART_CLASSES[computedColor.value]?.[computedVariant.value] ?? ''
);

const wrapperOutlineClass = computed(
  () => WRAPPER_OUTLINE[computedColor.value]?.[computedVariant.value] ?? ''
);

const sizeConfig = computed(() => SIZE_CONFIG[computedSize.value]);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const handleAction = item => {
  emit('action', item);
  isOpen.value = false;
};

const close = () => {
  isOpen.value = false;
};
</script>

<template>
  <div v-on-click-outside="close" class="relative inline-flex">
    <div
      class="inline-flex items-stretch rounded-lg overflow-hidden"
      :class="[
        wrapperOutlineClass,
        { 'opacity-50 pointer-events-none': disabled },
      ]"
    >
      <button
        type="button"
        class="inline-flex items-center gap-2 border-0 rounded-l-lg rounded-r-none transition-all duration-100 ease-out active:enabled:scale-[0.98] focus-visible:outline-none"
        :class="[buttonPartClasses, sizeConfig.main, sizeConfig.text]"
        @click="emit('click')"
      >
        <Icon v-if="icon" :icon="icon" class="flex-shrink-0 size-4" />
        <span v-if="label" class="min-w-0 truncate">{{ label }}</span>
      </button>

      <span class="w-px self-stretch bg-current opacity-20 flex-shrink-0" />

      <button
        type="button"
        class="inline-flex items-center justify-center border-0 rounded-r-lg rounded-l-none transition-all duration-100 ease-out flex-shrink-0 active:enabled:scale-[0.98] focus-visible:outline-none"
        :class="[buttonPartClasses, sizeConfig.toggle]"
        :aria-expanded="isOpen"
        aria-haspopup="true"
        @click="toggleDropdown"
      >
        <span
          class="transition-transform duration-150 flex-shrink-0"
          :class="[
            sizeConfig.chevron,
            isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
          ]"
        />
      </button>
    </div>

    <DropdownMenu
      v-if="isOpen && menuItems.length"
      :menu-items="menuItems"
      class="absolute left-0 top-full mt-1"
      @action="handleAction"
    />
  </div>
</template>
